/* eslint-disable camelcase */
import { TFilterOptions } from '@evinced-private/ui-common';

import { buildTupleString } from 'src/common/helpers/GQLRequestsHelper/buildTupleString';
import { removeSelectAllOption } from 'src/common/helpers/GQLRequestsHelper/removeSelectAllOption';
import {
	ConditionOperators,
	PagesAndFilter,
	PagesOrFilter,
	Wfa_Session_RunsAndFilter,
	Wfa_Session_RunsOrFilter
} from 'src/common/types/graphql.generated';

interface IOrFilter {
	custom_fields_ne_tuple_key?: {
		value?: string[];
		operator?: ConditionOperators;
	};
	custom_fields_eq_any_item_for_any_value?: {
		value?: string[][];
		operator?: ConditionOperators;
	};
}

interface IAndFilter<TOrFilter> {
	or?: TOrFilter[];
}

/**
 * Generic builder for AND filter with nested OR for labels that have "no option" selected.
 * For same label: (no label key) OR (label = selected values)
 * Uses the and: [ { or: [...] } ] structure.
 */
export const buildGenericAndFilter = <
	TAndFilter extends IAndFilter<TOrFilter>,
	TOrFilter extends IOrFilter
>(
	noOptionKeys: string[],
	filteredFilters: TFilterOptions
): TAndFilter[] | undefined => {
	if (noOptionKeys.length === 0) {
		return undefined;
	}

	const andFilters: TAndFilter[] = [];

	for (const labelKey of noOptionKeys) {
		const labelValues = filteredFilters[labelKey];
		const filteredLabelValues = removeSelectAllOption(labelValues ?? []);

		const orConditions: TOrFilter[] = [];

		// First OR condition: records without this label key
		orConditions.push({
			custom_fields_ne_tuple_key: {
				value: [labelKey],
				operator: ConditionOperators.All
			}
		} as TOrFilter);

		if (filteredLabelValues.length > 0) {
			// Include both original case and lowercase to handle case variations in database
			const tupleStrings: string[] = [];
			filteredLabelValues.forEach(({ value }) => {
				const valueStr = value.toString();
				const normalizedValue = valueStr.toLowerCase();
				tupleStrings.push(buildTupleString(labelKey, valueStr));
				if (normalizedValue !== valueStr) {
					tupleStrings.push(buildTupleString(labelKey, normalizedValue));
				}
			});

			// Second OR condition: records with matching label values
			orConditions.push({
				custom_fields_eq_any_item_for_any_value: {
					value: [tupleStrings],
					operator: ConditionOperators.All
				}
			} as TOrFilter);
		}

		andFilters.push({ or: orConditions } as TAndFilter);
	}

	return andFilters.length > 0 ? andFilters : undefined;
};

export const buildSessionsAndFilter = (
	noOptionKeys: string[],
	filteredFilters: TFilterOptions
): Wfa_Session_RunsAndFilter[] | undefined =>
	buildGenericAndFilter<Wfa_Session_RunsAndFilter, Wfa_Session_RunsOrFilter>(
		noOptionKeys,
		filteredFilters
	);

export const buildPagesAndFilter = (
	noOptionKeys: string[],
	filteredFilters: TFilterOptions
): PagesAndFilter[] | undefined =>
	buildGenericAndFilter<PagesAndFilter, PagesOrFilter>(noOptionKeys, filteredFilters);
